import { useEffect, useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { RowLink } from '@/components/admin/DataTable';
import ContactDialog from '@/components/admin/ContactDialog';
import { CONTACT_STATUSES, CONTACT_STATUS_STYLES } from '@/shared/constants';
import { CONTACT_FIELDS } from '@/shared/fields';
import { paged } from '@/shared/data';
import { formatRelativeTime } from '@/utils/date';

const { eq, or, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/messages', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'Mail', label: 'Messages', section: 'Support', profiles: ['Admin_c', 'Staff_c'], order: 55 };

const PAGE_SIZE = 20;

export default function AdminMessages() {
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [status, q]);

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('contact_messages_c').select(CONTACT_FIELDS);
    if (status !== 'all') query = query.where(eq('status_c', status));
    if (q) query = query.where(or(contains('Name', q), contains('email_c', q)));
    return query.orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged);
  }, [status, q, page]);

  const list = data?.rows ?? [];
  const filtered = status !== 'all' || !!q;

  function clear() {
    setSearch('');
    setStatus('all');
  }

  const columns = [
    {
      label: 'From',
      render: (m) => (
        <RowLink to={`/admin/messages/${m.Id}`} sub={m.email_c}>
          {m.Name}
        </RowLink>
      ),
    },
    {
      label: 'Message',
      className: 'hidden md:table-cell',
      render: (m) => <span className="line-clamp-2 max-w-md text-muted-foreground">{m.message_c}</span>,
    },
    { label: 'Status', render: (m) => <StatusBadge value={m.status_c} styles={CONTACT_STATUS_STYLES} /> },
    {
      label: 'Received',
      className: 'hidden sm:table-cell text-right text-muted-foreground',
      render: (m) => formatRelativeTime(m.CreatedOn),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Support"
        title="Messages"
        description="Questions from the contact form on the storefront."
        actions={
          <Button variant="outline" onClick={() => setCreateOpen(true)}>
            <ApperIcon name="Plus" size={16} />
            Log a message
          </Button>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search name or email…" />
        <FilterSelect value={status} onChange={setStatus} values={CONTACT_STATUSES} allLabel="All statuses" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Mail',
          title: filtered ? 'No messages match' : 'No messages yet',
          description: filtered ? 'Try another search or status.' : 'Messages from the contact form land here.',
          action: filtered ? clear : () => setCreateOpen(true),
          actionLabel: filtered ? 'Clear filters' : 'Log a message',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>

      <ContactDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={run} />
    </div>
  );
}
